import * as THREE from "three";
import type { Vec3 } from "@genjiball/protocol";
import { Keyboard } from "../input/keyboard.js";
import { PlayerVisual } from "../render/playerVisual.js";

const WALK_SPEED = 6;
const JUMP_VEL = 8;
const MAX_JUMPS = 2;
const GRAVITY = 22;
const AIR_CONTROL = 0.6;
const MOUSE_SENSITIVITY = 0.0022;
const CAPSULE_RADIUS = 0.4;
const PLATFORM_HALF = 15;
const GROUND_Y = 0;
const WALL_CLIMB_SPEED = 14.4;          // target vertical speed while climbing (m/s)
const WALL_CLIMB_RAMP_PER_SEC = 24;     // how fast vel.y converges toward target (units/s)
const VAULT_SPEED = 4;                  // horizontal speed of the auto-vault onto the platform
const WALL_PULL_SPEED = 6;              // how fast the player is pulled toward the wall surface
const WALL_CLIMB_REACH = 3.0;           // grab-the-wall distance from the wall surface
const WALL_CLIMB_MAX_ANGLE_DEG = 89;
const WALL_CLIMB_MAX_DOT = -Math.cos((WALL_CLIMB_MAX_ANGLE_DEG * Math.PI) / 180); // ≈ -0.017
const DEATH_PLANE_Y = -20;

export class LocalPlayer {
  readonly group: THREE.Group;
  readonly visual: PlayerVisual;
  pos: Vec3 = [0, 0, 0];
  vel: Vec3 = [0, 0, 0];
  yaw: number = 0;
  pitch: number = 0;
  grounded: boolean = true;
  jumpsRemaining: number = MAX_JUMPS;

  private prevSpaceDown: boolean = false;
  private prevWallClimbing: boolean = false;
  private lastWallNormal: Vec3 | null = null;

  constructor(color: string, spawn: Vec3) {
    this.pos = [...spawn] as Vec3;
    this.visual = new PlayerVisual(color);
    this.group = this.visual.group;
    this.group.position.set(...this.pos);
  }

  /** Kick off async character load. Capsule remains visible until ready. */
  loadCharacter(playerId: string): Promise<void> {
    return this.visual.loadCharacter(playerId);
  }

  applyMouse(dx: number, dy: number) {
    this.yaw -= dx * MOUSE_SENSITIVITY;
    this.pitch -= dy * MOUSE_SENSITIVITY;
    const PITCH_MAX = Math.PI / 2 - 0.05;
    if (this.pitch > PITCH_MAX) this.pitch = PITCH_MAX;
    if (this.pitch < -PITCH_MAX) this.pitch = -PITCH_MAX;
  }

  /** Returns the outward normal of the closest wall within reach, or null. */
  private wallClimbCheck(): Vec3 | null {
    // Player must be airborne, below platform top, above death plane.
    if (this.grounded) return null;
    if (this.pos[1] >= GROUND_Y) return null;
    if (this.pos[1] <= DEATH_PLANE_Y) return null;

    // Compute camera-forward (xz only — we don't climb up sloped looks).
    const cy = Math.cos(this.yaw);
    const sy = Math.sin(this.yaw);
    const fx = -sy;
    const fz = -cy;

    const candidates: { normal: Vec3; dist: number }[] = [];
    const r = WALL_CLIMB_REACH + CAPSULE_RADIUS;

    // +X face at x = PLATFORM_HALF
    if (this.pos[0] > PLATFORM_HALF && this.pos[0] - PLATFORM_HALF <= r) {
      candidates.push({ normal: [1, 0, 0], dist: this.pos[0] - PLATFORM_HALF });
    }
    // -X face at x = -PLATFORM_HALF
    if (this.pos[0] < -PLATFORM_HALF && -PLATFORM_HALF - this.pos[0] <= r) {
      candidates.push({ normal: [-1, 0, 0], dist: -PLATFORM_HALF - this.pos[0] });
    }
    // +Z face at z = PLATFORM_HALF
    if (this.pos[2] > PLATFORM_HALF && this.pos[2] - PLATFORM_HALF <= r) {
      candidates.push({ normal: [0, 0, 1], dist: this.pos[2] - PLATFORM_HALF });
    }
    // -Z face at z = -PLATFORM_HALF
    if (this.pos[2] < -PLATFORM_HALF && -PLATFORM_HALF - this.pos[2] <= r) {
      candidates.push({ normal: [0, 0, -1], dist: -PLATFORM_HALF - this.pos[2] });
    }

    if (candidates.length === 0) {
      this.debugFailReason = `not near any wall (pos=${this.pos[0].toFixed(1)},${this.pos[2].toFixed(1)})`;
      return null;
    }

    // Pick the closest candidate where the player is looking INTO the wall.
    let best: { normal: Vec3; dist: number } | null = null;
    let bestDot = 1;
    for (const c of candidates) {
      const dot = fx * c.normal[0] + fz * c.normal[2];
      if (dot < bestDot) bestDot = dot;
      if (dot < WALL_CLIMB_MAX_DOT) {
        if (!best || c.dist < best.dist) best = c;
      }
    }
    if (!best) {
      this.debugFailReason = `not looking into wall (best dot=${bestDot.toFixed(2)}, need < ${WALL_CLIMB_MAX_DOT.toFixed(2)})`;
    }
    return best ? best.normal : null;
  }

  /** Tracked by wallClimbCheck so main.ts can surface the reason in the console. */
  debugFailReason: string = "";

  step(kb: Keyboard, dt: number) {
    // -- Horizontal input -> target velocity --
    let forward = 0;
    let strafe = 0;
    if (kb.isDown("KeyW")) forward += 1;
    if (kb.isDown("KeyS")) forward -= 1;
    if (kb.isDown("KeyA")) strafe -= 1;
    if (kb.isDown("KeyD")) strafe += 1;

    const len = Math.hypot(forward, strafe);
    if (len > 0) {
      forward /= len;
      strafe /= len;
    }

    const cy = Math.cos(this.yaw);
    const sy = Math.sin(this.yaw);
    const targetVx = (-sy * forward + cy * strafe) * WALK_SPEED;
    const targetVz = (-cy * forward - sy * strafe) * WALK_SPEED;

    // Determine wall-climb state BEFORE horizontal velocity logic, so we can
    // suppress all WASD input while attached.
    const wallNormal = kb.isDown("Space") ? this.wallClimbCheck() : null;
    const wallClimbing = wallNormal !== null;

    // -- Apply horizontal velocity --
    if (wallClimbing && wallNormal) {
      // While climbing, drive the player onto the wall surface:
      //  - Perpendicular axis (into/out of the wall) is set to a velocity that
      //    pulls toward the wall surface up to WALL_PULL_SPEED.
      //  - Tangent axis (along the wall) decays softly toward zero.
      const offset = PLATFORM_HALF + CAPSULE_RADIUS + 0.01;
      const perpAxis: 0 | 2 = Math.abs(wallNormal[0]) > 0.5 ? 0 : 2;
      const tanAxis: 0 | 2 = perpAxis === 0 ? 2 : 0;
      const targetPerp =
        wallNormal[perpAxis] > 0 ? offset : -offset;
      const perpDelta = targetPerp - this.pos[perpAxis];

      if (Math.abs(perpDelta) > 0.01) {
        this.vel[perpAxis] =
          Math.sign(perpDelta) *
          Math.min(WALL_PULL_SPEED, Math.abs(perpDelta) / Math.max(dt, 0.001));
      } else {
        this.vel[perpAxis] = 0;
        this.pos[perpAxis] = targetPerp;
      }

      const decay = Math.min(1, WALL_CLIMB_RAMP_PER_SEC * dt);
      this.vel[tanAxis] *= 1 - decay;
    } else if (this.grounded) {
      this.vel[0] = targetVx;
      this.vel[2] = targetVz;
    } else {
      const k = AIR_CONTROL;
      this.vel[0] += (targetVx - this.vel[0]) * Math.min(1, k * dt * 10);
      this.vel[2] += (targetVz - this.vel[2]) * Math.min(1, k * dt * 10);
    }

    // -- Jump (edge-triggered on Space, while jumpsRemaining > 0) --
    const spaceDown = kb.isDown("Space");
    if (spaceDown && !this.prevSpaceDown && this.jumpsRemaining > 0) {
      this.vel[1] = JUMP_VEL;
      this.jumpsRemaining--;
      this.grounded = false;
    }
    this.prevSpaceDown = spaceDown;

    // -- Vertical velocity (wall-climb overrides gravity) --
    if (wallClimbing) {
      // Smoothly approach climb speed instead of snapping.
      const k = Math.min(1, WALL_CLIMB_RAMP_PER_SEC * dt);
      this.vel[1] += (WALL_CLIMB_SPEED - this.vel[1]) * k;
      // Reaching the top resets jumps so cresting gives a fresh double-jump.
      this.jumpsRemaining = MAX_JUMPS;
    } else if (!this.grounded) {
      this.vel[1] -= GRAVITY * dt;
    }

    // -- Vault: when wall-climb just ended near the top, fling the player onto
    // the platform interior so they don't immediately slide back off. --
    const justFinishedClimb = this.prevWallClimbing && !wallClimbing;
    if (justFinishedClimb && this.lastWallNormal && this.pos[1] > GROUND_Y - 1.0) {
      const n = this.lastWallNormal;
      this.vel[0] = -n[0] * VAULT_SPEED;
      this.vel[2] = -n[2] * VAULT_SPEED;
      this.vel[1] = Math.max(this.vel[1], 3); // small upward kick to clear the edge
      console.log(`[wall] VAULT at pos.y=${this.pos[1].toFixed(2)}`);
    }
    if (!this.prevWallClimbing && wallClimbing) {
      console.log(`[wall] START climbing at pos=[${this.pos[0].toFixed(2)}, ${this.pos[1].toFixed(2)}, ${this.pos[2].toFixed(2)}] vel.y=${this.vel[1].toFixed(2)}`);
    }
    if (this.prevWallClimbing && !wallClimbing) {
      console.log(`[wall] END climbing at pos=[${this.pos[0].toFixed(2)}, ${this.pos[1].toFixed(2)}, ${this.pos[2].toFixed(2)}] vel.y=${this.vel[1].toFixed(2)}`);
    }
    this.prevWallClimbing = wallClimbing;
    if (wallNormal) this.lastWallNormal = wallNormal;

    // -- Integrate --
    this.pos[0] += this.vel[0] * dt;
    this.pos[1] += this.vel[1] * dt;
    this.pos[2] += this.vel[2] * dt;

    // -- Land/collide with the cube. Ground snap (landing on top) takes
    // precedence over wall collision so that a player crossing pos.y=0 while
    // still over the platform's footprint snaps to the surface instead of
    // being teleported sideways to the nearest wall. Both are suppressed while
    // wall-climbing. --
    const insideInset =
      Math.abs(this.pos[0]) <= PLATFORM_HALF - CAPSULE_RADIUS &&
      Math.abs(this.pos[2]) <= PLATFORM_HALF - CAPSULE_RADIUS;

    const wasGrounded = this.grounded;
    if (!wallClimbing && insideInset && this.pos[1] <= GROUND_Y) {
      // Land on the platform top.
      this.pos[1] = GROUND_Y;
      this.vel[1] = 0;
      this.grounded = true;
      this.jumpsRemaining = MAX_JUMPS;
    } else {
      // Not landing on top — but if the capsule has tunnelled into the cube
      // body (XZ within ±15.4 AND pos.y below the top), push it out along the
      // nearest WALL face.
      if (!wallClimbing && this.pos[1] < GROUND_Y - 0.001) {
        const maxX = PLATFORM_HALF + CAPSULE_RADIUS;
        const maxZ = PLATFORM_HALF + CAPSULE_RADIUS;
        const insideX = this.pos[0] > -maxX && this.pos[0] < maxX;
        const insideZ = this.pos[2] > -maxZ && this.pos[2] < maxZ;
        if (insideX && insideZ) {
          const penX = Math.min(maxX - this.pos[0], this.pos[0] + maxX);
          const penZ = Math.min(maxZ - this.pos[2], this.pos[2] + maxZ);
          if (penX <= penZ) {
            if (this.pos[0] >= 0) {
              this.pos[0] = maxX;
              if (this.vel[0] < 0) this.vel[0] = 0;
            } else {
              this.pos[0] = -maxX;
              if (this.vel[0] > 0) this.vel[0] = 0;
            }
          } else {
            if (this.pos[2] >= 0) {
              this.pos[2] = maxZ;
              if (this.vel[2] < 0) this.vel[2] = 0;
            } else {
              this.pos[2] = -maxZ;
              if (this.vel[2] > 0) this.vel[2] = 0;
            }
          }
        }
      }
      if (wasGrounded) {
        // First frame of leaving the ground: cut horizontal momentum so the
        // player doesn't shoot off the edge and overshoot wall-climb reach.
        this.vel[0] *= 0.4;
        this.vel[2] *= 0.4;
      }
      this.grounded = false;
    }

    // -- Sync to scene graph --
    this.group.position.set(this.pos[0], this.pos[1], this.pos[2]);
    this.group.rotation.y = this.yaw;

    // -- Drive character animation from movement state --
    this.visual.applyMovementState({ vel: this.vel, grounded: this.grounded });
    this.visual.update(dt);
  }
}
